import { Archive, Plus, RotateCcw } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  CUSTOM_FIELD_MODULE_LABELS,
  CUSTOM_FIELD_MODULES,
  CUSTOM_FIELD_TYPE_LABELS,
  CUSTOM_FIELD_TYPES,
  CUSTOM_FIELD_VISIBILITIES,
  CUSTOM_FIELD_VISIBILITY_LABELS,
  customFieldSchema,
  isChoice,
  type CustomFieldModule,
  type CustomFieldType,
  type CustomFieldView,
  type CustomFieldVisibility,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Chip } from '../../../components/Chip';
import { Field } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { Segmented } from '../../../components/Segmented';
import { Select } from '../../../components/Select';
import { SkeletonLines } from '../../../components/Skeleton';
import { Switch } from '../../../components/Switch';
import { TagInput } from '../../../components/TagInput';
import { ARCHIVED_TONE } from '../../../components/status-tones';
import { ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { ToneChip } from '../../../components/Tone';
import { errorMessage } from '../../../lib/api-client';
import { formatNumber } from '../../../lib/format';
import { useModules } from '../../../lib/modules';
import { useCanEdit, useCanView } from '../../../stores/session';
import { FIELD_ROWS, customFieldsApi, useAdminCustomFields } from '../api/custom-fields-api';
import { ListTable, type ListColumn } from '../components/simple-list/ListTable';
import { FilterPills, ListNoMatch, ListSearch, ListToolbar, PanelActions, PanelFacts, PanelSection, SidePanel, SimpleListPage, usePanelMode } from '../components/simple-list/SimpleList';
import { useListAddress } from '../components/simple-list/use-list-address';
import { isPendingRow, pendingId, useRowChanges, type RowChanges } from '../components/simple-list/use-row-changes';

const PATH = '/admin/custom-fields';
const ADDING = 'new';
const STATUS_FILTERS = ['in_use', 'archived'] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];
const VISIBILITY_OPTIONS = CUSTOM_FIELD_VISIBILITIES.map((value) => ({ value, label: CUSTOM_FIELD_VISIBILITY_LABELS[value] }));
const TYPE_OPTIONS = CUSTOM_FIELD_TYPES.map((value) => ({ value, label: CUSTOM_FIELD_TYPE_LABELS[value] }));
const HELP_MAX = 200;

/**
 * ADMIN-13 / F19.11 — the organisation's own fields on candidates, jobs and requisitions (DECISIONS-LOG S29–S32 Q19), in
 * the simple-list pattern (Admin redesign batch 2, option A): the fields banded by where they sit, and the picked one
 * in the panel. Who sees it, Required and Archive change at once with Undo; the name, the choices and the help text are
 * edited and saved together. A choice that records still hold can't be taken out (the server says how many). The
 * Requisitions group shows only while Hire requests is on (#13). Admin only (`admin_config`).
 */
export function CustomFieldsPage(): ReactNode {
  const canView = useCanView('admin_config');
  const canEdit = useCanEdit('admin_config');
  const modules = useModules();
  const fields = useAdminCustomFields(canView);
  const changes = useRowChanges(FIELD_ROWS);
  const mode = usePanelMode();
  const [address, setAddress] = useListAddress(PATH);
  const shownModules = CUSTOM_FIELD_MODULES.filter((module) => module !== 'requisition' || modules.requisitions);
  const status = STATUS_FILTERS.find((value) => value === address.show) ?? null;
  const onModule = shownModules.find((value) => value === address.tab) ?? null;
  const query = address.q ?? '';

  if (!canView) return <ForbiddenState resource="the custom fields" contact="Your admin" />;
  if (fields.isPending) return <SkeletonLines count={10} />;
  if (fields.error && !fields.data) return <ServerErrorState onRetry={() => void fields.refetch()} />;

  const all = fields.data.filter((field) => shownModules.includes(field.module));
  const shown = all.filter((row) => matchesStatus(row, status) && (onModule === null || row.module === onModule) && matchesQuery(row, query));
  const isAdding = address.open === ADDING && canEdit;
  const open = all.find((row) => row.id === address.open) ?? null;
  const close = (): void => setAddress({ open: undefined });

  const columns: ListColumn<CustomFieldView>[] = [
    { key: 'kind', header: 'Kind of answer', isWideOnly: true, render: (row) => CUSTOM_FIELD_TYPE_LABELS[row.fieldType] },
    { key: 'who', header: 'Who sees it', isWideOnly: true, render: (row) => CUSTOM_FIELD_VISIBILITY_LABELS[row.visibility] },
    { key: 'filled', header: 'Filled in', isNumeric: true, render: (row) => formatNumber(row.values) },
  ];

  return (
    <SimpleListPage
      crumb="Admin · Hiring setup"
      title="Custom fields"
      total={all.length}
      lede="Your own fields on candidates and jobs, shown on staff screens only in a “More details” card; candidate fields can be searched. A field is archived, never deleted, so values already recorded stay on the record and in exports."
      actions={
        canEdit && (
          <Button variant="solid" icon={Plus} onClick={() => setAddress({ open: ADDING })}>
            Add a field
          </Button>
        )
      }
      toolbar={
        <ListToolbar search={<ListSearch label="Find a field" value={query} onChange={(next) => setAddress({ q: next })} />}>
          <FilterPills
            label="Show fields that are"
            value={status}
            options={[
              { value: 'in_use', label: 'In use', count: all.filter((row) => !row.isArchived).length },
              { value: 'archived', label: 'Archived', count: all.filter((row) => row.isArchived).length },
            ]}
            onChange={(next) => setAddress({ show: next ?? undefined })}
          />
          <FilterPills
            label="Show fields on"
            value={onModule}
            options={shownModules.map((module) => ({ value: module, label: CUSTOM_FIELD_MODULE_LABELS[module] }))}
            onChange={(next) => setAddress({ tab: next ?? undefined })}
          />
        </ListToolbar>
      }
      panel={
        isAdding ? (
          <SidePanel mode={mode} label="Add a field" title="Add a field" onClose={close}>
            <AddField modules={shownModules} knownIds={new Set(all.map((row) => row.id))} changes={changes} onAdded={(id) => setAddress({ open: id, show: undefined, q: undefined, tab: undefined })} />
          </SidePanel>
        ) : (
          open && (
            <SidePanel mode={mode} label={`Field: ${open.label}`} title={open.label} sub={<FieldChips row={open} />} onClose={close}>
              <FieldPanel key={open.id} row={open} canEdit={canEdit} changes={changes} />
            </SidePanel>
          )
        )
      }
    >
      <ListTable
        label="Custom fields"
        name={{ header: 'Field', label: (row) => row.label, render: (row) => <FieldName row={row} /> }}
        columns={columns}
        groups={shownModules.map((module) => ({ key: module, label: CUSTOM_FIELD_MODULE_LABELS[module], rows: shown.filter((row) => row.module === module) }))}
        idOf={(row) => row.id}
        selectedId={open?.id ?? null}
        onOpen={(row) => setAddress({ open: row.id })}
        isMuted={(row) => row.isArchived}
        isPending={(row) => isPendingRow(row.id)}
        phoneLine={(row) => `${CUSTOM_FIELD_TYPE_LABELS[row.fieldType]} · ${CUSTOM_FIELD_VISIBILITY_LABELS[row.visibility]}`}
        trailing={(row) =>
          canEdit &&
          row.isArchived && (
            <Button size="sm" variant="ghost" icon={RotateCcw} onClick={() => setArchived(changes, row, false)}>
              Restore
            </Button>
          )
        }
        empty={
          all.length === 0 ? (
            <span>No fields yet. Add one to record something of your own on candidates or jobs.</span>
          ) : (
            <ListNoMatch what="field" query={query} onClear={() => setAddress({ q: undefined, show: undefined, tab: undefined })} />
          )
        }
      />
    </SimpleListPage>
  );
}

function FieldName({ row }: { readonly row: CustomFieldView }): ReactNode {
  return (
    <>
      {row.label}
      {row.isRequired && <Chip plain>Required</Chip>}
      {row.isArchived && <ToneChip tone={ARCHIVED_TONE}>Archived</ToneChip>}
    </>
  );
}

function FieldChips({ row }: { readonly row: CustomFieldView }): ReactNode {
  return (
    <>
      {row.isRequired && <Chip plain>Required</Chip>}
      {row.isArchived && <ToneChip tone={ARCHIVED_TONE}>Archived</ToneChip>}
    </>
  );
}

interface FieldDraft {
  readonly label: string;
  readonly options: readonly string[];
  readonly helpText: string;
}

function FieldPanel({ row, canEdit, changes }: { readonly row: CustomFieldView; readonly canEdit: boolean; readonly changes: RowChanges<CustomFieldView> }): ReactNode {
  const saved: FieldDraft = { label: row.label, options: row.options, helpText: row.helpText ?? '' };
  const [draft, setDraft] = useState<FieldDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const shownDraft = draft ?? saved;
  const hasChoices = isChoice(row.fieldType);
  const label = shownDraft.label.trim();
  const helpText = shownDraft.helpText.trim();
  const problem = draftProblem(shownDraft, hasChoices);
  const isChanged = label !== row.label || helpText !== (row.helpText ?? '') || (hasChoices && JSON.stringify(shownDraft.options) !== JSON.stringify(row.options));
  const isLocked = !canEdit || row.isArchived;

  const edit = (next: Partial<FieldDraft>): void => {
    setError(null);
    setDraft({ ...shownDraft, ...next });
  };

  const save = (): void => {
    if (problem !== null || !isChanged) return;
    const typed = shownDraft;
    const change = { label, helpText: helpText === '' ? null : helpText, ...(hasChoices ? { options: [...typed.options] } : {}) };
    const before = { label: row.label, helpText: row.helpText, ...(hasChoices ? { options: [...row.options] } : {}) };
    setDraft(null);
    changes.changeWithUndo(
      {
        id: row.id,
        patch: change,
        send: customFieldsApi.change(row.id, change),
        failure: "The field wasn't saved",
        // A refusal (a choice records still hold) gives back what was typed, with the reason beside the choices.
        onRefused: (cause) => {
          setDraft(typed);
          setError(errorMessage(cause));
        },
      },
      { message: `${label} saved`, undo: { id: row.id, patch: before, send: customFieldsApi.change(row.id, before), failure: "The field wasn't put back" } },
    );
  };

  const setVisibility = (value: string): void => {
    const visibility = CUSTOM_FIELD_VISIBILITIES.find((option) => option === value);
    if (!visibility || visibility === row.visibility) return;
    const before = row.visibility;
    changes.changeWithUndo(
      { id: row.id, patch: { visibility }, send: customFieldsApi.change(row.id, { visibility }), failure: "Who sees it wasn't changed" },
      { message: `${row.label}: ${CUSTOM_FIELD_VISIBILITY_LABELS[visibility].toLowerCase()}`, undo: { id: row.id, patch: { visibility: before }, send: customFieldsApi.change(row.id, { visibility: before }), failure: "Who sees it wasn't put back" } },
    );
  };

  const setRequired = (isRequired: boolean): void =>
    changes.changeWithUndo(
      { id: row.id, patch: { isRequired }, send: customFieldsApi.change(row.id, { isRequired }), failure: "The field wasn't changed" },
      { message: isRequired ? `${row.label} is required` : `${row.label} is optional`, undo: { id: row.id, patch: { isRequired: !isRequired }, send: customFieldsApi.change(row.id, { isRequired: !isRequired }), failure: "The change wasn't put back" } },
    );

  return (
    <>
      <PanelSection>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            save();
          }}
        >
          <Field label="Name" error={label.length > 0 && label.length < 2 ? 'At least 2 characters.' : undefined}>
            {({ inputId, describedBy, isInvalid }) => (
              <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} value={shownDraft.label} maxLength={80} disabled={isLocked} onChange={(event) => edit({ label: event.target.value })} />
            )}
          </Field>
        </form>
        <PanelFacts
          facts={[
            { label: 'On', value: CUSTOM_FIELD_MODULE_LABELS[row.module] },
            { label: 'Kind of answer', value: CUSTOM_FIELD_TYPE_LABELS[row.fieldType] },
            { label: 'Filled in', value: `${formatNumber(row.values)} ${row.values === 1 ? 'record' : 'records'}` },
          ]}
        />
        <p>The kind of answer can’t change once a field is made: the values already recorded would no longer fit.</p>
        {hasChoices && (
          <Field label="Choices" hint="Type one and press Enter; at least two. A choice records still hold can’t be taken out." error={error ?? (shownDraft.options.length < 2 ? 'Give at least two choices.' : undefined)}>
            {({ inputId, describedBy, isInvalid }) => (
              <TagInput id={inputId} describedBy={describedBy} isInvalid={isInvalid} value={shownDraft.options} onChange={(options) => edit({ options })} maxItems={50} maxLength={80} disabled={isLocked} placeholder="Add a choice" />
            )}
          </Field>
        )}
        <Field label="Help text" isOptional hint="Shown under the field." error={hasChoices ? undefined : (error ?? undefined)}>
          {({ inputId, describedBy, isInvalid }) => (
            <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} value={shownDraft.helpText} maxLength={HELP_MAX} disabled={isLocked} onChange={(event) => edit({ helpText: event.target.value })} />
          )}
        </Field>
        {canEdit && !row.isArchived && (
          <PanelActions>
            <Button variant="ghost" size="sm" disabled={!isChanged} onClick={() => setDraft(null)}>
              Discard
            </Button>
            <Button variant="solid" size="sm" disabled={!isChanged || problem !== null} onClick={save}>
              Save
            </Button>
          </PanelActions>
        )}
      </PanelSection>
      <PanelSection title="Who sees it">
        <Field label="Shown to" hint="Anyone else sees “—” where the value would be.">
          {({ inputId, describedBy }) => <Select id={inputId} describedBy={describedBy} label={`Who sees ${row.label}`} options={VISIBILITY_OPTIONS} value={row.visibility} disabled={isLocked} onChange={setVisibility} />}
        </Field>
        <Switch checked={row.isRequired} disabled={isLocked} label="Required when the record is edited" onChange={setRequired} />
      </PanelSection>
      {canEdit && (
        <PanelSection>
          <p>{row.isArchived ? 'Archived: it is off every record, and the values already recorded are kept.' : 'Archiving takes it off every record. Values already recorded are kept, and Restore brings it back.'}</p>
          <PanelActions>
            {row.isArchived ? (
              <Button variant="outline" size="sm" icon={RotateCcw} onClick={() => setArchived(changes, row, false)}>
                Restore
              </Button>
            ) : (
              <Button variant="ghost" size="sm" icon={Archive} onClick={() => setArchived(changes, row, true)}>
                Archive
              </Button>
            )}
          </PanelActions>
        </PanelSection>
      )}
    </>
  );
}

function AddField({ modules, knownIds, changes, onAdded }: { readonly modules: readonly CustomFieldModule[]; readonly knownIds: ReadonlySet<string>; readonly changes: RowChanges<CustomFieldView>; readonly onAdded: (id: string) => void }): ReactNode {
  const [module, setModule] = useState<CustomFieldModule>('candidate');
  const [label, setLabel] = useState('');
  const [fieldType, setFieldType] = useState<CustomFieldType>('short_text');
  const [options, setOptions] = useState<readonly string[]>([]);
  const [visibility, setVisibility] = useState<CustomFieldVisibility>('all_staff');
  const [isRequired, setIsRequired] = useState(false);
  const [helpText, setHelpText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = async (): Promise<void> => {
    const parsed = customFieldSchema.safeParse({ module, label, fieldType, options: isChoice(fieldType) ? [...options] : [], visibility, isRequired, helpText: helpText.trim() === '' ? null : helpText });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Check the field.');
      return;
    }
    const input = parsed.data;
    setError(null);
    setLabel('');
    try {
      const saved = await changes.add(placeholderOf(input), () => customFieldsApi.create(input, knownIds));
      setOptions([]);
      setHelpText('');
      setIsRequired(false);
      onAdded(saved.id);
    } catch (cause) {
      // A refusal keeps what was typed (review W20).
      setLabel(input.label);
      setError(errorMessage(cause));
    }
  };

  return (
    <PanelSection>
      <Segmented label="On" value={module} options={modules.map((value) => ({ value, label: CUSTOM_FIELD_MODULE_LABELS[value] }))} onChange={setModule} />
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void add();
        }}
      >
        <Field label="Name" isRequired error={error ?? undefined}>
          {({ inputId, describedBy, isInvalid }) => (
            <Input id={inputId} autoFocus aria-describedby={describedBy} isInvalid={isInvalid} value={label} maxLength={80} placeholder="e.g. Shift preference" onChange={(event) => setLabel(event.target.value)} />
          )}
        </Field>
      </form>
      <Field label="Kind of answer" hint="It can’t change once the field is made.">
        {({ inputId, describedBy }) => (
          <Select
            id={inputId}
            describedBy={describedBy}
            label="Kind of answer"
            options={TYPE_OPTIONS}
            value={fieldType}
            onChange={(value) => {
              const next = CUSTOM_FIELD_TYPES.find((option) => option === value);
              if (next) setFieldType(next);
            }}
          />
        )}
      </Field>
      {isChoice(fieldType) && (
        <Field label="Choices" hint="Type one and press Enter; at least two.">
          {({ inputId, describedBy }) => <TagInput id={inputId} describedBy={describedBy} value={options} onChange={setOptions} maxItems={50} maxLength={80} placeholder="e.g. Day" />}
        </Field>
      )}
      <Field label="Who sees it" hint="Anyone else sees “—” where the value would be.">
        {({ inputId, describedBy }) => (
          <Select
            id={inputId}
            describedBy={describedBy}
            label="Who sees it"
            options={VISIBILITY_OPTIONS}
            value={visibility}
            onChange={(value) => {
              const next = CUSTOM_FIELD_VISIBILITIES.find((option) => option === value);
              if (next) setVisibility(next);
            }}
          />
        )}
      </Field>
      <Field label="Help text" isOptional hint="Shown under the field.">
        {({ inputId, describedBy }) => <Input id={inputId} aria-describedby={describedBy} value={helpText} maxLength={HELP_MAX} onChange={(event) => setHelpText(event.target.value)} />}
      </Field>
      <Switch checked={isRequired} onChange={setIsRequired} label="Required when the record is edited" />
      <PanelActions>
        <span />
        <Button variant="solid" size="sm" icon={Plus} disabled={label.trim().length < 2} onClick={() => void add()}>
          Add field
        </Button>
      </PanelActions>
    </PanelSection>
  );
}

function setArchived(changes: RowChanges<CustomFieldView>, row: CustomFieldView, isArchived: boolean): void {
  changes.changeWithUndo(
    { id: row.id, patch: { isArchived }, send: customFieldsApi.change(row.id, { isArchived }), failure: isArchived ? "The field wasn't archived" : "The field wasn't restored" },
    {
      message: isArchived ? `${row.label} archived — recorded values are kept` : `${row.label} is back on records`,
      undo: { id: row.id, patch: { isArchived: !isArchived }, send: customFieldsApi.change(row.id, { isArchived: !isArchived }), failure: "The change wasn't put back" },
    },
  );
}

/** What stops the panel's Save: the same rules the server applies. */
function draftProblem(draft: FieldDraft, hasChoices: boolean): string | null {
  const label = draft.label.trim();
  if (label.length < 2) return 'At least 2 characters.';
  if (hasChoices && draft.options.length < 2) return 'Give at least two choices.';
  if (hasChoices && new Set(draft.options).size !== draft.options.length) return 'Each choice can be listed once.';
  return null;
}

function placeholderOf(input: { readonly module: CustomFieldModule; readonly label: string; readonly fieldType: CustomFieldType; readonly options: readonly string[]; readonly visibility: CustomFieldVisibility; readonly isRequired: boolean; readonly helpText: string | null }): CustomFieldView {
  return {
    id: pendingId(),
    module: input.module,
    label: input.label,
    fieldType: input.fieldType,
    options: input.options,
    isRequired: input.isRequired,
    minNumber: null,
    maxNumber: null,
    maxLength: null,
    visibility: input.visibility,
    helpText: input.helpText,
    sortOrder: Number.MAX_SAFE_INTEGER,
    isArchived: false,
    values: 0,
    version: 0,
  };
}

function matchesStatus(row: CustomFieldView, status: StatusFilter | null): boolean {
  if (status === 'in_use') return !row.isArchived;
  if (status === 'archived') return row.isArchived;
  return true;
}

function matchesQuery(row: CustomFieldView, query: string): boolean {
  const needle = query.trim().toLowerCase();
  return needle === '' || row.label.toLowerCase().includes(needle) || row.options.some((option) => option.toLowerCase().includes(needle));
}
